/**
 * Public routes — code-split so each public page is its own chunk.
 *
 * Home is eager-loaded (it's the most common landing URL and users arrive here
 * first, so we avoid the extra round-trip of fetching a chunk). All other
 * public pages are lazy: the user only downloads the code for the page they
 * actually visit.
 */
import { lazy, Suspense } from "react";
import { Navigate, Route, useParams } from "react-router-dom";

// Eager — home is the default landing; inlining avoids extra RTT on first visit
import Home from "../pages/public/home";

// Lazy — each page becomes its own webpack chunk
const Explore         = lazy(() => import("../pages/public/explore"));
const Pricing         = lazy(() => import("../pages/public/pricing"));
const About           = lazy(() => import("../pages/public/about"));
const Contact         = lazy(() => import("../pages/public/contact"));
const Blog            = lazy(() => import("../pages/public/blog"));
const BlogPost        = lazy(() => import("../pages/public/blogPost"));
const Privacy         = lazy(() => import("../pages/public/privacy"));
const Terms           = lazy(() => import("../pages/public/terms"));
const BusinessProfile = lazy(() =>
  import("../pages/business/profile").then((m) => ({ default: m.default || m.BusinessProfile }))
);

/** Minimal loader shown while a page chunk downloads. */
function PageLoader() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

const lazyPage = (Element) => (
  <Suspense fallback={<PageLoader />}>
    <Element />
  </Suspense>
);

/** Must be registered before `business/:slug` so /business/dashboard is not treated as a public slug. */
function LegacyBusinessDashboardRedirect() {
  const { locale } = useParams();
  return <Navigate to={`/${locale}/dashboard`} replace />;
}

const PublicRoutes = () => {
  return (
    <>
      {/* Home — eager (no Suspense) */}
      <Route index element={<Home />} />

      {/* All other public pages — lazy */}
      <Route path="explore"  element={lazyPage(Explore)} />
      <Route path="services" element={lazyPage(Explore)} />
      <Route path="pricing"  element={lazyPage(Pricing)} />
      <Route path="about"    element={lazyPage(About)} />
      <Route path="blog/:categorySlug/:postSlug" element={lazyPage(BlogPost)} />
      <Route path="blog"     element={lazyPage(Blog)} />
      <Route path="contact"  element={lazyPage(Contact)} />
      <Route path="privacy"  element={lazyPage(Privacy)} />
      <Route path="terms"    element={lazyPage(Terms)} />

      {/* More specific paths before `business/:slug` */}
      <Route path="business/dashboard" element={<LegacyBusinessDashboardRedirect />} />
      <Route path="business/profile/:id" element={lazyPage(BusinessProfile)} />
      <Route path="business/:slug"       element={lazyPage(BusinessProfile)} />
    </>
  );
};

export default PublicRoutes;
